import { useMutation } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import type { OrpcClient } from '@/orpc/client'
import { describeDoseSpotClinicianKind } from '@/utils/doseSpotClinicianKind'
import { useOrpc } from './useClient'

export type SetPractitionerDoseSpotClinicianInput = Parameters<
  OrpcClient['setPractitionerDoseSpotClinician']
>[0]

/**
 * Writes or clears a member's DoseSpot clinician id. This mapping *is* the prescribing
 * grant — saving one enables Prescribe for that member, clearing it revokes immediately —
 * so the notifications say so rather than reporting a generic save.
 *
 * The middleware validates the id against DoseSpot before persisting; its rejection
 * messages are written to be shown as-is.
 */
export function useSetPractitionerDoseSpotClinician() {
  const orpc = useOrpc()

  return useMutation(
    orpc.setPractitionerDoseSpotClinician.mutationOptions({
      onSuccess: (result) => {
        showNotification({
          color: 'green',
          title: result.clinicianId ? 'Prescribing enabled' : 'Prescribing revoked',
          // The kind is DoseSpot's verdict on the id, detected by the middleware (PD-1592):
          // this toast is where the super admin sees it, since the modal closes on save.
          message: result.clinicianKind
            ? `DoseSpot clinician ${result.clinicianId} linked as ${describeDoseSpotClinicianKind(result.clinicianKind)}.`
            : 'The DoseSpot clinician mapping was removed.',
        })
      },
      onError: (error) => {
        showNotification({
          color: 'red',
          title: 'DoseSpot mapping not saved',
          message: error instanceof Error ? error.message : 'Unknown error',
        })
      },
    }),
  )
}
